import React, { useState } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertTriangle, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * iter114s.89 — Integration health pings.
 *
 * Sits above the rotation panel; surfaces an at-a-glance "is this
 * integration actually working" view that the operator could only
 * answer before by triggering a real customer flow (place a quote
 * to test Freightcom, try a checkout for Converge, send an invite
 * for Resend, etc.). Each ping is a backend-driven real auth check
 * against the live service — `utils/integration_ping.py` for the
 * per-service semantics.
 *
 * Behaviour notes:
 * - "Ping" buttons hit `POST /api/admin/integrations/ping/{name}`.
 * - "Ping all" hits `POST /api/admin/integrations/ping` which runs
 *   the four checks in parallel server-side (≤ ~600ms total).
 * - Results are kept in component state so navigating away and back
 *   shows a fresh blank state — admin always sees a real, current
 *   result (no stale "last green at 4pm" mirage).
 * - Each row keeps its own latency + message + last-pinged time so
 *   operators can paste a screenshot to support if a flow misbehaves.
 */
const INTEGRATIONS = [
  {
    name: 'resend',
    label: 'Resend',
    desc: 'Transactional email transport (welcome / proof / reset).',
  },
  {
    name: 'converge',
    label: 'Converge / Elavon',
    desc: 'Payment authorisation + capture.',
  },
  {
    name: 'freightcom',
    label: 'Freightcom',
    desc: 'Live shipping rates at quote + checkout.',
  },
  {
    name: 'sentry',
    label: 'Sentry',
    desc: 'Error tracking + alerting (background).',
  },
];

const StatusPill = ({ state, latency }) => {
  if (state === 'idle') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500 italic">
        Not pinged yet
      </span>
    );
  }
  if (state === 'pinging') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pinging…
      </span>
    );
  }
  if (state === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
        <CheckCircle2 className="w-3.5 h-3.5" /> Healthy
        {typeof latency === 'number' && (
          <span className="ml-1 text-gray-500 font-normal">({latency}ms)</span>
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
      <AlertTriangle className="w-3.5 h-3.5" /> Failing
      {typeof latency === 'number' && (
        <span className="ml-1 text-gray-500 font-normal">({latency}ms)</span>
      )}
    </span>
  );
};

export const IntegrationHealthChecks = () => {
  // Each entry: { state: 'idle'|'pinging'|'ok'|'fail', latency, message, pingedAt }
  const [results, setResults] = useState(() =>
    Object.fromEntries(INTEGRATIONS.map(i => [i.name, { state: 'idle' }]))
  );
  const [allRunning, setAllRunning] = useState(false);

  const recordResult = (name, payload) => {
    setResults(prev => ({
      ...prev,
      [name]: {
        state: payload.ok ? 'ok' : 'fail',
        latency: payload.latency_ms,
        message: payload.message,
        pingedAt: new Date().toISOString(),
      },
    }));
  };

  const pingOne = async (name) => {
    setResults(prev => ({ ...prev, [name]: { ...prev[name], state: 'pinging' } }));
    try {
      const res = await axios.post(
        `${API}/admin/integrations/ping/${name}`,
        {},
        { withCredentials: true }
      );
      recordResult(name, res.data);
      if (res.data?.ok) {
        toast.success(`${name} — healthy (${res.data.latency_ms}ms)`);
      } else {
        toast.error(`${name} — failing: ${res.data?.message || 'unknown'}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'unknown error';
      recordResult(name, { ok: false, latency_ms: 0, message: msg });
      toast.error(`${name} ping failed: ${msg}`);
    }
  };

  const pingAll = async () => {
    setAllRunning(true);
    setResults(prev =>
      Object.fromEntries(
        INTEGRATIONS.map(i => [i.name, { ...prev[i.name], state: 'pinging' }])
      )
    );
    try {
      const res = await axios.post(
        `${API}/admin/integrations/ping`,
        {},
        { withCredentials: true }
      );
      const data = res.data || {};
      Object.entries(data).forEach(([name, payload]) => recordResult(name, payload));
      const okCount = Object.values(data).filter(r => r?.ok).length;
      toast.success(`Pinged all — ${okCount}/${INTEGRATIONS.length} healthy`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'unknown error';
      toast.error(`Ping all failed: ${msg}`);
      INTEGRATIONS.forEach(i =>
        recordResult(i.name, { ok: false, latency_ms: 0, message: msg })
      );
    } finally {
      setAllRunning(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6"
      data-testid="integration-health-checks"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#2F5D3A] flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Integration Health
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Real auth + reachability checks against each live service.
            No customer flow needs to be triggered — these probes go directly
            to the integration provider with a minimal request and report
            whether the configured credentials are accepted right now.
          </p>
        </div>
        <button
          type="button"
          onClick={pingAll}
          disabled={allRunning}
          className="inline-flex items-center gap-1.5 bg-[#4A7C59] hover:bg-[#3a6346] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded transition-colors"
          data-testid="integration-ping-all"
        >
          {allRunning ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Pinging…
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" /> Ping all
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {INTEGRATIONS.map((it) => {
          const r = results[it.name];
          return (
            <div
              key={it.name}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50/40"
              data-testid={`integration-card-${it.name}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-semibold text-sm text-[#1F3D2C]">
                    {it.label}
                  </div>
                  <div className="text-xs text-gray-500">{it.desc}</div>
                </div>
                <button
                  type="button"
                  onClick={() => pingOne(it.name)}
                  disabled={r.state === 'pinging' || allRunning}
                  className="text-xs font-medium text-[#4A7C59] hover:text-[#2F5D3A] underline-offset-2 hover:underline disabled:opacity-50 disabled:no-underline shrink-0"
                  data-testid={`integration-ping-${it.name}`}
                >
                  {r.state === 'pinging' ? 'Pinging…' : 'Ping now'}
                </button>
              </div>
              <div className="mt-1">
                <StatusPill state={r.state} latency={r.latency} />
              </div>
              {r.message && (
                <div
                  className={`mt-2 text-xs leading-snug rounded p-2 font-mono break-words ${
                    r.state === 'ok'
                      ? 'bg-green-50 text-green-900 border border-green-100'
                      : 'bg-red-50 text-red-900 border border-red-100'
                  }`}
                  data-testid={`integration-message-${it.name}`}
                >
                  {r.message}
                </div>
              )}
              {r.pingedAt && (
                <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">
                  Pinged {new Date(r.pingedAt).toLocaleString()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationHealthChecks;
